'use client';
import axios from 'axios';
import { useState } from 'react';

// export const BASE_URL = "https://engine.solutionson.ai";
export const BASE_URL = process.env.NODE_ENV === 'development' 
    ? "http://localhost:8080" 
    : "https://engine.solutionson.ai";


const generateOptions = (url: string) => {
    const options: any = {
        url,
        // headers: {
        //   'content-type': 'application/x-www-form-urlencoded',
        // },
    };

    return options;
};

const useRequest = (endpoint?: string) => {
    const client = axios.create({
      baseURL: BASE_URL,
      withCredentials: true,
    });

    const request = {
        get: async (url: string, query?: TObject) => {
            const options = generateOptions(url);
            options.params = query;

            return await client.get(url, options);
        },
        post: async (url: string, body: TObject) => {
            const options = generateOptions(url);
            options.data = body;

            return client.post(options.url, body, options);
        },
        put: async (url: string, body: TObject) => {
            const options = generateOptions(url);
            options.data = body;

            return client.put(options);
        },
        patch: async (url: string, body: TObject) => {
            const options = generateOptions(url);
            options.data = body;

            return await client.patch(options);
        },
        delete: async (url: string, query: TObject) => {
            const options = generateOptions(url);
            options.params = query;

            return await client.delete(options);
        },
        upload: async (url: string, body: TObject) => {
            const options = generateOptions(url);

            return axios.post(url, body, options);
        },
    };
    return { request };
};

export type TObject = { [key: string]: any };
interface IResponse {
    data: TObject;
}

export type PatchCallback = (value: TObject) => void;

export default function initServices(endpoint?: string) {
    return {
        useFetch: function useFetch<Data = any>(
            url: string,
            defaultData?: Data,
            showSnackBarOnError: boolean = true,
        ): [[ Data | undefined, any ], (query?: TObject, callback?: (value: any) => void) => void, boolean] {
            const { request } = useRequest(endpoint);
            const [loading, setLoading] = useState(false); //TODO Check

            const [response, setResponse] = useState([
                defaultData,
                undefined,
            ]);

            const fetchFn = (query?: TObject, callback?: (value: any) => void ) => {
                setLoading(true);
                return request
                    .get(url, query)
                    .then((response: any) => {
                        if (!response.data.error) {
                            setResponse([
                                response.data,
                                undefined,
                            ]);
                            setLoading(false);
                            callback && callback({ data: response.data });
                        } else {
                            errorHandler(response);
                            callback && callback({ error: response.data.error });
                        }
                    })
                    .catch((error: any) => {
                        errorHandler(error);
                        callback && callback({ error: error.response });
                    });
            };

            const errorHandler = (error: any) => {
                setResponse([
                    defaultData,
                    error.response,
                ]);
                setLoading(false);
                if (showSnackBarOnError) {
                    // setGlobalError(error);
                }
            };
//@ts-ignore
            return [response, fetchFn, loading];
        },

        usePost: function usePost<ResponseType = any>(
            url: string,
            callback?: (value: any) => void,
        ): [ResponseType, (body: TObject) => void, boolean, any] {
            const { request } = useRequest(endpoint);
            const [data, setData] = useState<ResponseType>({} as ResponseType);
            const [error, setError] = useState<any>(undefined);
            const [loading, setLoading] = useState(false);

            const postFn = (
                body: any,
                callback2?: (value: any) => void 
            ) => {
                setLoading(true);

                request
                    .post(url, body)
                    .then((response: any) => {
                        setData(response.data);
                        setLoading(false);
                        callback && callback({ data: response.data });
                        callback2 && callback2({ data: response.data });
                    })
                    .catch((error: any) => {
                        setLoading(false);
                        setError(error);
                        callback && callback({ error: error.response });
                        callback2 && callback2({ error: error.response });
                    });
            };

            return [data, postFn, loading, error];
        },

        usePatch: function usePatch<DataType>(
            url: string,
            callback?: (value: any) => void,
        ): [{ [key: string]: any }, (data: TObject) => void, boolean, any] {
            const { request } = useRequest(endpoint);
            const [data, setData] = useState({});
            const [error, setError] = useState<any>(null);
            const [loading, setLoading] = useState(false);

            const patchFn = (body: TObject) => {
                setLoading(true);

                request
                    .patch(url, body)
                    .then((response: IResponse) => {
                        const data = response.data || { success: true };
                        setData(data);
                        setLoading(false);
                        callback && callback({ data });
                    })
                    .catch((error: any) => {
                        console.error(error);
                        setLoading(false);
                        setError(error);
                        callback && callback({ error: error.response });
                    });
            };

            return [data, patchFn, loading, error?.response];
        },

        useDelete: function useDelete(
            url: string,
            callback?: (value: any) => void,
        ): [{ [key: string]: any }, (query?: TObject) => void, Boolean] {
            const { request } = useRequest(endpoint);
            const [data, setData] = useState({});
            const [loading, setLoading] = useState(false);

            const deleteFn = (query: any) => {
                setLoading(true);

                request
                    .delete(url, query)
                    .then((response: IResponse) => {
                        setData(response.data);
                        setLoading(false);
                        callback && callback({ data: response.data });
                    })
                    .catch((error: any) => {
                        console.error(error);
                        setLoading(false);
                        callback && callback({ error: error.response });
                    });
            };

            return [data, deleteFn, loading];
        },

        usePut: function usePut(
            url: string,
            callback?: (val: { error?: any; data?: TObject }) => void,
        ): [{ [key: string]: any }, (query?: TObject) => void, Boolean] {
            const { request } = useRequest(endpoint);
            const [data, setData] = useState({});
            const [loading, setLoading] = useState(false);

            const putFn = (body: any) => {
                setLoading(true);

                request
                    .put(url, body)
                    .then((response: IResponse) => {
                        setData(response.data);
                        setLoading(false);
                        callback && callback({ data: response.data });
                    })
                    .catch((error: any) => {
                        console.error(error);
                        setLoading(false);
                        callback && callback({ error: error.response });
                    });
            };

            return [data, putFn, loading];
        },

        useUpload: function usePost<ResponseType = any>(
            url: string,
            callback?: (value: any) => void,
        ): [ResponseType, (body: any) => void, boolean, any] {
            const { request } = useRequest(endpoint);
            const [data, setData] = useState<ResponseType>({} as ResponseType);
            const [error, setError] = useState<any>(undefined);
            const [loading, setLoading] = useState(false);

            const postFn = (body: any) => {
                setLoading(true);

                request
                    .upload(url, body)
                    .then((response: any) => {
                        setData(response.data);
                        setLoading(false);
                        callback && callback({ data: response.data });
                    })
                    .catch((error: any) => {
                        setLoading(false);
                        setError(error);
                        callback && callback({ error: error.response });
                    });
            };

            return [data, postFn, loading, error];
        },
    }
}
